import React, { useMemo, useState, useEffect } from 'react';
import {
  TIPOS_EVENTO_TAREO,
  normalizarFechaTareo,
  claveOverrideTareo,
} from '../utils/tareoEventoEfectivo';

/** Columnas visibles según tipo de planilla del empleado. */
function ladosBoletaEmpleado(emp) {
  const tipo = String(
    emp?.tipo_planilla || emp?.datosEmpleado?.tipo_planilla || ''
  ).toLowerCase().trim();

  if (tipo === 'solo_interna') {
    return { muestraPlame: false, muestraInterna: true };
  }
  if (tipo === 'dual') {
    return { muestraPlame: true, muestraInterna: true };
  }
  if (tipo === 'solo_plame' || tipo === 'plame') {
    return { muestraPlame: true, muestraInterna: false };
  }

  // Sin tipo explícito: inferir por sueldo interno (mismo criterio que el resto de la planilla)
  const tieneInterna = emp?.sueldo_interno != null && Number(emp.sueldo_interno) > 0;
  if (tieneInterna) {
    return { muestraPlame: true, muestraInterna: true };
  }

  return { muestraPlame: true, muestraInterna: false };
}

/**
 * Modal para evento distinto por boleta (PLAME / interna) por día.
 */
const EventosBoletaOverrideModal = ({
  isOpen,
  onClose,
  empleados = [],
  tareosPeriodo = [],
  overridesMap = {},
  onGuardar,
  guardando = false,
}) => {
  const [empleadoId, setEmpleadoId] = useState(null);
  const [draft, setDraft] = useState({}); // key -> { plame, interna }

  useEffect(() => {
    if (!isOpen) return;
    setDraft({});
    if (empleados.length > 0 && !empleadoId) {
      setEmpleadoId(empleados[0].id);
    }
  }, [isOpen, empleados, empleadoId]);

  const empleadoActual = useMemo(
    () => (empleados || []).find((e) => Number(e.id) === Number(empleadoId)) || null,
    [empleados, empleadoId]
  );

  const { muestraPlame, muestraInterna } = useMemo(
    () => ladosBoletaEmpleado(empleadoActual),
    [empleadoActual]
  );

  const diasEmpleado = useMemo(() => {
    if (!empleadoId) return [];
    const idNum = Number(empleadoId);
    const porFecha = new Map();
    (tareosPeriodo || []).forEach((t) => {
      if (Number(t.empleado_id) !== idNum) return;
      const fecha = normalizarFechaTareo(t.fecha);
      if (!fecha) return;
      if (!porFecha.has(fecha)) {
        porFecha.set(fecha, {
          fecha,
          tipo_tareo: String(t.tipo_evento || 'asistio').toLowerCase(),
        });
      }
    });
    return Array.from(porFecha.values()).sort((a, b) => a.fecha.localeCompare(b.fecha));
  }, [empleadoId, tareosPeriodo]);

  const valorSelect = (fecha, lado) => {
    const key = claveOverrideTareo(empleadoId, fecha);
    if (draft[key] && draft[key][lado] !== undefined) {
      return draft[key][lado] ?? '';
    }
    const row = overridesMap[key];
    if (!row) return '';
    const v = lado === 'plame' ? row.tipo_evento_plame : row.tipo_evento_interna;
    return v || '';
  };

  const setValor = (fecha, lado, valor) => {
    const key = claveOverrideTareo(empleadoId, fecha);
    if (!key) return;
    setDraft((prev) => {
      const base = prev[key] || {
        plame: overridesMap[key]?.tipo_evento_plame || null,
        interna: overridesMap[key]?.tipo_evento_interna || null,
      };
      return {
        ...prev,
        [key]: {
          ...base,
          [lado]: valor === '' ? null : valor,
        },
      };
    });
  };

  const labelTipo = (tipo) => {
    const found = TIPOS_EVENTO_TAREO.find((t) => t.value === tipo);
    return found?.label || tipo || '—';
  };

  const textoAyuda = () => {
    if (muestraPlame && muestraInterna) {
      return 'Vacío = usar el tareo real. Ej.: PLAME = Vacaciones, Interna = Asistió (o al revés).';
    }
    if (muestraInterna) {
      return 'Vacío = usar el tareo real. Solo boleta interna para este personal.';
    }
    return 'Vacío = usar el tareo real. Solo boleta PLAME para este personal.';
  };

  const handleGuardar = async () => {
    const cambios = [];
    const eliminar = [];

    Object.entries(draft).forEach(([key, vals]) => {
      const [empIdStr, fecha] = key.split('|');
      const empId = Number(empIdStr);
      const emp = (empleados || []).find((e) => Number(e.id) === empId);
      const lados = ladosBoletaEmpleado(emp);
      const existente = overridesMap[key] || {};

      const plame = lados.muestraPlame
        ? (vals.plame || null)
        : (existente.tipo_evento_plame || null);
      const interna = lados.muestraInterna
        ? (vals.interna || null)
        : (existente.tipo_evento_interna || null);

      if (!plame && !interna) {
        eliminar.push({ empleado_id: empId, fecha });
      } else {
        cambios.push({
          empleado_id: empId,
          fecha,
          tipo_evento_plame: plame,
          tipo_evento_interna: interna,
        });
      }
    });

    if (cambios.length === 0 && eliminar.length === 0) {
      onClose();
      return;
    }

    await onGuardar({ overrides: cambios, eliminar });
    setDraft({});
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-teal-600 to-cyan-600 p-5 text-white flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">Eventos distintos por boleta</h2>
            <p className="text-teal-100 text-sm">
              Define un tipo de día distinto por boleta sin cambiar el tareo
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-lg px-3 py-1 text-xl"
          >
            ✕
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Empleado</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={empleadoId || ''}
              onChange={(e) => setEmpleadoId(Number(e.target.value) || null)}
            >
              {empleados.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.nombre_completo || emp.nombre || `Empleado ${emp.id}`}
                  {emp.documento || emp.numero_documento
                    ? ` (${emp.documento || emp.numero_documento})`
                    : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 text-sm text-teal-900">
            {textoAyuda()}
          </div>

          {diasEmpleado.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No hay tareos en el período para este empleado.</p>
          ) : (
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-700">
                  <tr>
                    <th className="px-3 py-2 text-left">Fecha</th>
                    <th className="px-3 py-2 text-left">Tareo real</th>
                    {muestraPlame && <th className="px-3 py-2 text-left">PLAME</th>}
                    {muestraInterna && <th className="px-3 py-2 text-left">Interna</th>}
                  </tr>
                </thead>
                <tbody>
                  {diasEmpleado.map((dia) => {
                    const tieneCambio = Boolean(
                      (muestraPlame && valorSelect(dia.fecha, 'plame'))
                      || (muestraInterna && valorSelect(dia.fecha, 'interna'))
                    );
                    return (
                      <tr
                        key={dia.fecha}
                        className={`border-t border-gray-100 ${tieneCambio ? 'bg-amber-50' : ''}`}
                      >
                        <td className="px-3 py-2 font-medium whitespace-nowrap">{dia.fecha}</td>
                        <td className="px-3 py-2 text-gray-600">{labelTipo(dia.tipo_tareo)}</td>
                        {muestraPlame && (
                          <td className="px-3 py-2">
                            <select
                              className="w-full border border-gray-300 rounded px-2 py-1"
                              value={valorSelect(dia.fecha, 'plame')}
                              onChange={(e) => setValor(dia.fecha, 'plame', e.target.value)}
                            >
                              <option value="">Usar tareo</option>
                              {TIPOS_EVENTO_TAREO.map((t) => (
                                <option key={`p-${t.value}`} value={t.value}>{t.label}</option>
                              ))}
                            </select>
                          </td>
                        )}
                        {muestraInterna && (
                          <td className="px-3 py-2">
                            <select
                              className="w-full border border-gray-300 rounded px-2 py-1"
                              value={valorSelect(dia.fecha, 'interna')}
                              onChange={(e) => setValor(dia.fecha, 'interna', e.target.value)}
                            >
                              <option value="">Usar tareo</option>
                              {TIPOS_EVENTO_TAREO.map((t) => (
                                <option key={`i-${t.value}`} value={t.value}>{t.label}</option>
                              ))}
                            </select>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="bg-gray-50 px-5 py-4 border-t flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
            disabled={guardando}
          >
            Cerrar
          </button>
          <button
            type="button"
            onClick={handleGuardar}
            disabled={guardando || Object.keys(draft).length === 0}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
          >
            {guardando ? 'Guardando…' : 'Guardar y recalcular'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EventosBoletaOverrideModal;
